import {
  BaseChartResp,
  ExportPerformanceBySegmentResp,
  KpiDataDto,
} from '@data-visualization/performance-analysis-line-chart/Types';
import { ColumnType, DataCell } from '@ecosystem/export-api';
import { ExcelFormatter, Fact, formatKpiForExport, Kpi } from '@ecosystem/formatters';
import { formatLabelIntoRevenuePriceTitle } from '@utils/currencyUtils/CurrencyUtils';
import { getKpiDisplayName } from '@hooks/export/ExportUtils';

export const formatPerformanceAnalysisLineChartExcel = (
  data: ExportPerformanceBySegmentResp,
  currencyLabel: string,
  volume?: string,
): DataCell[][] => {
  return data.kpiDataDto
    .flatMap((dto) => flatMapExportPerformanceKpi(dto, currencyLabel, volume))
    .sort(compareExportPerformanceKpiByKpiType)
    .map((kpi) => kpi.dataRow);
};

const flatMapExportPerformanceKpi = (
  kpiDataDto: KpiDataDto,
  currencyLabel: string,
  volume?: string,
): FlattenedExportPerformanceKpi[] => {
  return kpiDataDto.kpiData.map((kpi) => {
    const headlessCells = kpi.marketAverage.map((periodData) => mapMarketAverageToDataCell(periodData, kpi.kpiType));

    return {
      kpiTypeOrder: kpiTypeOrder[kpi.kpiType],
      dataRow: prependLeadingCells(headlessCells, kpiDataDto.brandName, kpi.kpiType, currencyLabel, volume),
    };
  });
};

const prependLeadingCells = (
  headlessCells: DataCell[],
  brandName: string,
  kpiType: string,
  currencyLabel: string,
  volume?: string,
) => {
  const brandCell = { value: brandName, type: ColumnType.Text };
  const kpiNameCell = {
    value: formatLabelIntoRevenuePriceTitle(getKpiDisplayName(kpiType, volume), currencyLabel),
    type: ColumnType.Text,
  };

  return [brandCell, kpiNameCell, ...headlessCells];
};

const mapMarketAverageToDataCell = (periodData: BaseChartResp, kpiType: string): DataCell => {
  switch (kpiType) {
    case 'Market Revenue':
    case 'My Revenue':
    case 'Market Price':
    case 'My Price':
      return { value: periodData.value, type: ColumnType.Currency };
    case 'Market Volume':
    case 'My Volume':
      return { value: periodData.value, type: ColumnType.Number };
    case 'My Revenue Share':
    case 'My Volume Share':
    case 'My Revenue Mix':
    case 'My Volume Mix': {
      const value = formatKpiForExport(Kpi.revenueShare, { value: periodData.value } as Fact, new ExcelFormatter())[0];
      return { value: value, type: ColumnType.Percentage };
    }
  }

  throw new Error(`Unknown kpiType: ${kpiType}`);
};

const compareExportPerformanceKpiByKpiType = (
  kpi1: FlattenedExportPerformanceKpi,
  kpi2: FlattenedExportPerformanceKpi,
): number => {
  return kpi1.kpiTypeOrder - kpi2.kpiTypeOrder;
};

type FlattenedExportPerformanceKpi = {
  kpiTypeOrder: number;
  dataRow: DataCell[];
};

interface KpiTypeOrder {
  [key: string]: number;
}

const kpiTypeOrder: KpiTypeOrder = {
  'Market Revenue': 1,
  'Market Volume': 2,
  'Market Price': 3,
  'My Revenue': 4,
  'My Revenue Share': 5,
  'My Volume': 6,
  'My Volume Share': 7,
  'My Price': 8,
  'My Revenue Mix': 9,
  'My Volume Mix': 10,
};
